/**
 * English (en) — base locale
 * All other locales must provide every key defined here.
 * Keys use dot notation: namespace.section.key
 */
const en = {
  // ── Meta ──────────────────────────────────────────────────
  meta: {
    appName:  'Maternally',
    tagline:  'impacting lives, one mother at a time',
    language: 'English',
    dir:      'ltr',
    locale:   'en-US',
  },

  // ── Common ────────────────────────────────────────────────
  common: {
    install: { iosHint: "Add Maternally to your Home Screen: tap Share, then “Add to Home Screen”." },
    continue:      'Continue',
    back:          'Back',
    save:          'Save',
    cancel:        'Cancel',
    close:         'Close',
    loading:       'Loading…',
    error:         'Something went wrong. Please try again.',
    required:      'Required',
    optional:      'optional',
    seeAll:        'See all',
    viewAll:       'View all',
    submit:        'Submit',
    send:          'Send',
    next:          'Next',
    skip:          'Skip for now',
    done:          'Done',
    confirm:       'Confirm',
    delete:        'Delete',
    edit:          'Edit',
    search:        'Search',
    noResults:     'No results found',
    minutes:       '{{n}} min',
    today:         'Today',
    yesterday:     'Yesterday',
  },

  // ── Navigation ────────────────────────────────────────────
  nav: {
    home:      'Home',
    mood:      'Mood',
    companion: 'Companion',
    learn:     'Learn',
    profile:   'Profile',
  },

  // ── Auth ──────────────────────────────────────────────────
  verifyEmail: {
    working:       'Confirming your email…',
    oneMoment:     'One moment.',
    doneTitle:     'Your email is confirmed',
    doneBody:      'You can sign in now.',
    signIn:        'Sign in',
    failedTitle:   'We could not confirm that link',
    missingToken:  'This link is missing its verification code.',
    failed:        'This link is invalid or has expired.',
    failedHelp:    'Verification links expire. Sign in and we will send a new one.',
    backToSignIn:  'Back to sign in',
  },
  severity: {
    none: 'none', minimal: 'minimal', mild: 'mild', moderate: 'moderate',
    moderately_severe: 'moderately severe', severe: 'severe', crisis: 'crisis',
  },
  auth: {
    restoreAccount: {
      prompt: "This account is scheduled for deletion. Sign in again to restore it.",
      action: "Restore my account",
      working: "Restoring…",
      failed: "We could not restore this account.",
    },
    resendVerification: "Send me a new verification link",
    sending: "Sending…",
    verificationResent: "If that account needs verifying, a new link is on its way.",
    signIn:            'Sign in',
    signOut:           'Sign out',
    register:          'Create account',
    email:             'Email address',
    password:          'Password',
    confirmPassword:   'Confirm password',
    forgotPassword:    'Forgot password?',
    resetPassword:     'Reset password',
    welcomeBack:       'Welcome back',
    signInSubtitle:    'Sign in to continue your journey',
    newToApp:          'New to Maternally?',
    alreadyHaveAccount:'Already have an account?',
    createAccount:     'Create an account',
    checkEmail:        'Check your email',
    checkEmailBody:    "We've sent a verification link to {{email}}. Open it to activate your account.",
    backToSignIn:      'Back to sign in',
    passwordRules: {
      length:    'At least 10 characters',
      uppercase: 'One uppercase letter',
      lowercase: 'One lowercase letter',
      number:    'One number',
      special:   'One special character',
    },
    privacyNote: 'By registering you agree to our Terms of Service and Privacy Policy. Your health data is encrypted with AES-256 and visible only to your care team.',
    immediateSupport: 'Need immediate support? →',
  },

  // ── Onboarding ────────────────────────────────────────────
  onboarding: {
    dontAskAgain: "Don't ask again",
    welcome:       'Welcome to Maternally',
    whereAreYou:   'Where are you on your journey right now?',
    stages: {
      trying:     { label: 'Trying to conceive', sub: 'On the journey to pregnancy' },
      pregnant:   { label: 'Pregnant',            sub: 'Currently expecting' },
      postpartum: { label: 'Postpartum',          sub: 'After birth (up to 2 years)' },
      loss:       { label: 'Pregnancy loss',      sub: 'Miscarriage, stillbirth, or infant loss' },
    },
    howFarAlong:   'How far along are you?',
    whenDidYouGive:'When did you give birth?',
    takYourTime:   'Take your time',
    lossMessage:   'We are so sorry for your loss. You only need to share what you are comfortable with.',
    gestWeeks:     'Gestational age (weeks)',
    approximate:   'Approximate is fine',
    birthDate:     'Birth date (approximate)',
    whatCallYou:   'What should we call you?',
    nameOptional:  'Entirely optional — we can use your email instead.',
    firstName:     'First name',
    nameEncrypted: '🔒 Your name is encrypted and only visible to you and your care team.',
    fewThings:     'A few quick things',
    consent: {
      clinical:     { label: 'Clinical data sharing',       desc: 'I understand that my assessment results and mood data will be shared with my assigned care team to provide support.' },
      push:         { label: 'Wellness reminders',          desc: 'Send me gentle reminders to complete my daily check-ins and assessments. (You can turn these off any time.)' },
      research:     { label: 'Research contribution',       desc: 'Allow anonymised, de-identified data to be used to improve perinatal mental health outcomes. This never includes your name or contact details.' },
    },
    letsStart:     "I'm ready — let's start 🌱",
    privacyNote:   'By continuing you agree to our Terms of Service and Privacy Policy. Your data is encrypted at rest with AES-256.',
  },

  // ── Dashboard ─────────────────────────────────────────────
  dashboard: {
    greeting: {
      morning:   'Good morning',
      afternoon: 'Good afternoon',
      evening:   'Good evening',
    },
    stages: {
      trying:     'Trying to conceive',
      pregnant:   'Pregnant',
      postpartum: 'Postpartum',
      loss:       'Pregnancy loss',
    },
    riskLevels: {
      low:      'low risk',
      moderate: 'moderate risk',
      high:     'high risk',
      crisis:   '⚠ Crisis support active',
    },
    crisis: {
      supportTitle: "Things sound hard right now. Support is here whenever you want it.",
      title:    'You are not alone. Immediate support is available.',
      more:     'More resources',
    },
    quickActions: {
      logMood:    'Log mood',
      talkItOut:  'Talk it out',
      checkIn:    'Check-in',
      resources:  'Resources',
    },
    yourWeek:         'Your week',
    recentCheckIns:   'Recent check-ins',
    carePlan:         'Your care plan',
    carePlanSub:      'View your goals and today\'s wellness tasks',
    chartLegend: {
      mood:    'Mood',
      anxiety: 'Anxiety',
    },
  },

  // ── Mood ─────────────────────────────────────────────────
  mood: {
    title:        'How are you today?',
    subtitle:     'This takes about 60 seconds and helps us support you better.',
    overallMood:  'Overall mood',
    scale: {
      veryLow:  'Very low',
      low:      'Low',
      okay:     'Okay',
      good:     'Good',
      great:    'Great',
    },
    energy:       'Energy level',
    anxiety:      'Anxiety level',
    sleep:        'Sleep last night',
    energyLow:    'Exhausted',
    energyHigh:   'Energised',
    anxietyLow:   'Calm',
    anxietyHigh:  'Very anxious',
    sleepLow:     '0 hrs',
    sleepHigh:    '12 hrs',
    tags:         'How would you describe it?',
    note:         'Anything else on your mind?',
    notePlaceholder:'Write freely — this is private to you and encrypted…',
    save:         "Save today's check-in",
    saved:        'Logged!',
    savedSub:     'Your check-in for today has been saved.',
    logAnother:   'Log another entry',
    moodTags: [
      'Anxious', 'Tired', 'Overwhelmed', 'Hopeful',
      'Grateful', 'Tearful', 'Disconnected', 'Bonding well',
      'Lonely', 'Supported', 'Brain fog', 'Present',
    ],
  },

  // ── Assessments ───────────────────────────────────────────
  assessment: {
    title:        'Screening',
    instruments: {
      EPDS: 'Edinburgh Postnatal Depression Scale',
      GAD7: 'Anxiety Screening (GAD-7)',
      PHQ9: 'Depression Screening (PHQ-9)',
    },
    intro: {
      description: 'This is a validated screening tool used by clinicians to understand how you have been feeling over the past 7 days. There are no right or wrong answers.',
      questions:   '{{n}} questions',
      time:        'Takes about 5 minutes',
      shared:      'Results are shared with your care team',
      begin:       'Begin screening',
      notNow:      'Not now',
    },
    inPast7Days:  'In the past 7 days…',
    crisisNotice: 'Your answer to this question helps us make sure you have the right support.',
    submitting:   'Reviewing your responses…',
    result: {
      score:         'Score',
      whatHappensNext: 'What happens next',
      backHome:      'Back to home',
      talkCompanion: 'Talk to your companion',
      crisis: {
        title:   'You are not alone — help is available right now',
        teamAlerted: 'Your care team has been alerted and will reach out shortly.',
      },
    },
  },

  // ── Companion ─────────────────────────────────────────────
  companion: {
    welcome: "Hello, I'm here with you. This is a safe space to share how you're feeling — whatever is on your mind. How are you doing today?",
    name:        'Your companion',
    subtitle:    'Confidential · Clinically informed · Always available',
    needHelp:    'Need help now?',
    placeholder: 'Share what\'s on your mind…',
    disclaimer:  'This is not a substitute for professional care.',
    immediateHelp: 'Need immediate help?',
    starters: [
      "I've been feeling really overwhelmed lately",
      "I'm having trouble sleeping and it's affecting my mood",
      "I feel like I'm not bonding with my baby the way I expected",
      "I'm anxious about becoming a mother",
      "I'm not sure if what I'm feeling is normal",
    ],
    crisis: {
      title:     'Immediate support',
      message:   'You are not alone. These resources are free and available right now.',
    },
  },

  // ── Content ───────────────────────────────────────────────
  content: {
    title:       'Learn',
    subtitle:    'Evidence-based resources for your journey',
    search:      'Search articles, exercises…',
    filters: {
      all:       'All',
      article:   'Article',
      exercise:  'Exercise',
      audio:     'Audio',
      module:    'Module',
    },
    categories: ['Anxiety', 'Sleep', 'Bonding', 'Self-care', 'Relationships', 'Grief'],
    empty:       'No content found. Try a different search.',
    minRead:     '{{n}} min read',
    minDuration: '{{n}} min',
  },

  // ── Care plan ─────────────────────────────────────────────
  care: {
    title:          'Care plan',
    noplan:         'No care plan yet',
    noplanSub:      'Complete your first assessment and a care manager will create a personalised plan for you within 48 hours.',
    startAssessment:'Start your first check-in',
    goals:          'Your goals',
    todaysTasks:    "Today's tasks",
    complete:       'Mark complete',
    completed:      'Completed',
    nextReview:     'Next review with your care team:',
    categories: {
      self_care:   'Self care',
      therapy:     'Therapy',
      medication:  'Medication',
      social:      'Social',
      exercise:    'Exercise',
      sleep:       'Sleep',
      nutrition:   'Nutrition',
      other:       'Other',
    },
  },

  // ── Profile ───────────────────────────────────────────────
  profile: {
    deleteAccount: {
      title: "Delete account",
      sub: "Remove your account and everything in it",
      confirmTitle: "Delete your account?",
      confirmBody: "This phone will be emptied straight away — anyone who picks it up will find nothing. Your data is kept for 30 days in case you change your mind; you can sign back in with your email and password to restore it. After that it is destroyed.",
      retainNote: "We keep two things and would rather say so: a record that an account existed and was deleted, with no personal details, and any crisis that was escalated to a clinician.",
      cancel: "Keep my account",
      confirm: "Delete my account",
      working: "Deleting…",
      failed: "We could not delete your account. Please try again, or email support@maternally.care.",
    },
    completeSetup: 'Finish setting up',
    completeSetupSub: "Add your name and stage whenever you're ready",
    notSetYet: 'Not set yet',
    title:        'Profile',
    myJourney:    'My journey',
    stage:        'Stage',
    epdsScreening:'EPDS screening',
    takeNow:      'Take now →',
    carePlan:     'Care plan',
    view:         'View →',
    notifications: 'Notifications',
    dailyReminders:'Daily mood reminders',
    dailyRemindersSub: 'Gentle nudge each morning',
    smsAlerts:    'SMS alerts',
    smsAlertsSub: 'Care team messages via text',
    privacy:      'Privacy & data',
    research:     'Contribute to research',
    researchSub:  'Anonymised data only — helps improve care for all mothers',
    encryption:   'Data encryption',
    compliance:   'Compliance',
    support:      'Support',
    crisisResources: 'Crisis resources',
    appVersion:   'App version',
    signOut:      'Sign out',
    stageName: {
      trying:     'Trying to conceive',
      pregnant:   'Pregnant',
      postpartum: 'Postpartum',
      loss:       'Pregnancy loss',
    },
  },

  // ── Crisis ────────────────────────────────────────────────
  crisis: {
    title:   'You are not alone',
    subtitle:'Help is available right now — free, confidential, and just one call or text away.',
    fact:    'Perinatal mood and anxiety disorders are the most common complication of pregnancy and the postpartum period — affecting 1 in 5 mothers. With the right support, recovery is possible and very common.',
    factTitle: 'You matter — and so does getting support',
    backToApp:  '← Back to Maternally',
  },

  // ── Language switcher ─────────────────────────────────────
  languageSwitcher: {
    label:   'Language',
    english: 'English',
    arabic:  'العربية',
    french:  'Français',
  },
};

// Deep-widen all string literal types to `string` so other locales
// (e.g. Arabic) can provide their own translated values for every key.
// Exception: meta.dir must stay a literal 'ltr' | 'rtl' union since
// the i18n engine and RTL CSS depend on checking its exact value.
type Widen<T> = T extends string
  ? string
  : T extends readonly (infer U)[]
    ? Widen<U>[]
    : T extends object
      ? { [K in keyof T]: K extends 'dir' ? T[K] : Widen<T[K]> }
      : T;

export type Locale = Widen<typeof en>;
export default en as Locale;
